// Design tokens for the Logic Decomposer — RE-ANCHORED to the Jarvis design system.
//
// History: these were locked to "Logic Decomposer v3 - Dev Handoff.md" §2 (Plus Jakarta
// Sans, navy #1a0f4a, blue #3b6fff, purple #7c4dff), which made LD a design-component
// island distinct from the rest of Jarvis / Migration Architect. The handoff itself said
// "If a formal design system is bound, re-anchor these to its tokens before implementation."
// That system is now the app's Tailwind config (frontend/tailwind.config.js): font-sans
// Inter, super-dark-purple #221551, MA actions amber-500. These tokens are re-anchored to it
// so every LD file that imports `color`/`font` renders as Jarvis chrome without a per-file
// rewrite. Prefer Tailwind classes on new/edited markup; these constants exist only for the
// legacy inline-style call sites still being migrated. (Task 1218070214136694, subtask
// "Unify LD visual language with Jarvis".)

export const color = {
  navy:        '#221551',   // Jarvis super-dark-purple — headers, rails, dark chrome
  ink:         '#1e1b3a',   // primary text, dark buttons, dark code blocks
  appBg:       '#f9fafb',   // Tailwind gray-50 — main content area
  cardBorder:  '#e5e7eb',   // Tailwind gray-200
  textMuted:   '#6b7280',   // Tailwind gray-500
  accent:      '#f59e0b',   // Jarvis amber-500 — MA actions, selected states, links
  purple:      '#8b5cf6',   // Jarvis lavender-deep — gradients, accents (NOT disposition color)
  surface:     '#ffffff',
  rowSelected: '#fffbeb',   // amber-50 — selected row tint, matches MA
} as const;

export const shadow = {
  card: '0 1px 3px #1e1b3a10',
  panel: '-8px 0 32px #1e1b3a1a',
} as const;

export const radius = { chip: 8, control: 10, card: 14 } as const;

export const font = {
  ui: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
} as const;

// ── Disposition taxonomy ──────────────────────────────────────────────────────────────────
// SINGLE consultant-facing vocabulary: the six-outcome / two-axis engine model —
// Box 1-4 (WHERE) + Consolidate / Decommission / Queue. This is what the Portfolio,
// Framework Mapping, Review Queue, and the Artifact panel all say now, so the consultant
// never has to translate between "Migrate/Document" and "Box 1-4" in their head.
// (Task 1218070214136694, subtask "One consultant-facing disposition taxonomy".)
//
// `Outcome` is defined once, in framework/types.ts, and re-exported here so the visual layer
// (Pills, Portfolio) and the engine layer share one source of truth.
export type { Outcome } from './framework/types';
import type { Outcome as OutcomeT } from './framework/types';

export const OUTCOME_ORDER: OutcomeT[] = ['box1', 'box2', 'box3', 'box4', 'consolidate', 'decommission', 'queue'];

export const OUTCOME_LABEL: Record<OutcomeT, string> = {
  box1: 'Box 1', box2: 'Box 2', box3: 'Box 3', box4: 'Box 4',
  consolidate: 'Consolidate', decommission: 'Decommission', queue: 'Queue',
};

// What each box targets — the one-line "what happens to it", shown as the pill detail / row
// target-state. Matches recordsToPortfolio TARGET and Framework Mapping's box definitions.
export const OUTCOME_TARGET: Record<OutcomeT, string> = {
  box1: 'Full SDLC rebuild', box2: 'Governed / light SDLC', box3: 'Platform migration',
  box4: 'Retain & improve', consolidate: 'Merge into survivor', decommission: 'Retire', queue: 'Resolution queue',
};

// Outcome swatches on the Jarvis palette. Box 1-4 ride the super-dark-purple → lavender ramp
// (WHERE axis — a set, not a red/green judgement); Consolidate / Decommission / Queue keep their
// semantic hues (merge / retire / unresolved). NOT the Cloud Portfolio BUY/HOLD/SELL colors.
export const outcomeColor: Record<OutcomeT, { bg: string; fg: string }> = {
  box1:         { bg: '#221551', fg: '#221551' },   // Box 1 — deepest (full rebuild)
  box2:         { bg: '#5b3fc4', fg: '#4c2fa8' },   // Box 2 — governed / light
  box3:         { bg: '#8b5cf6', fg: '#6d28d9' },   // Box 3 — platform migration
  box4:         { bg: '#a78bfa', fg: '#6d28d9' },   // Box 4 — retain & improve
  consolidate:  { bg: '#0891b2', fg: '#155e75' },   // merge-into-survivor
  decommission: { bg: '#dc2626', fg: '#991b1b' },   // retire
  queue:        { bg: '#64748b', fg: '#334155' },   // unresolved
};

// LEGACY 4-box alias — retained ONLY as a documented export vocabulary for clients who still
// ask for "Migrate / Document" language on exports. Not the primary UI taxonomy. New UI reads
// `Outcome` / OUTCOME_LABEL above. (subtask: "Keep the old 4-box names only as a documented
// export alias … not as the primary UI.")
export type Disposition = 'Migrate' | 'Document' | 'Consolidate' | 'Decommission' | 'Queue';

/** Legacy export mapping: six outcomes → the old 4-box words, for the export alias only. */
export const OUTCOME_TO_LEGACY: Record<OutcomeT, Disposition> = {
  box1: 'Migrate', box2: 'Migrate', box3: 'Migrate', box4: 'Document',
  consolidate: 'Consolidate', decommission: 'Decommission', queue: 'Queue',
};

/**
 * Normalize any raw disposition string the backend/analyze path might emit to a single `Outcome`.
 * Accepts the engine's box keys directly (box1..box4/consolidate/decommission/queue) AND the
 * legacy 4-box words (migrate → box1, document → box4) so an older run still lands in the six-
 * outcome taxonomy. Unknown / not-yet-scored → 'queue'.
 */
export function outcomeFromRaw(raw: string | null | undefined): OutcomeT {
  const s = String(raw || '').toLowerCase();
  switch (s) {
    case 'box1': case 'box2': case 'box3': case 'box4':
    case 'consolidate': case 'decommission': case 'queue':
      return s as OutcomeT;
    case 'migrate': return 'box1';      // legacy Migrate collapsed 1/2/3 — surface as Box 1 by default
    case 'document': return 'box4';     // legacy Document ↔ Box 4 (retain & improve)
    default: return 'queue';
  }
}

export const disposition: Record<Disposition, { bg: string; fg: string }> = {
  Migrate:      { bg: '#8b5cf6', fg: '#6d28d9' },
  Document:     { bg: '#a78bfa', fg: '#6d28d9' },
  Consolidate:  { bg: '#0891b2', fg: '#155e75' },   // merge-into-survivor — distinct from Decommission (retire)
  Decommission: { bg: '#dc2626', fg: '#991b1b' },
  Queue:        { bg: '#64748b', fg: '#334155' },
};

export type Complexity = 'High' | 'Medium' | 'Low';

export const complexity: Record<Complexity, string> = {
  High:   '#dc2626',
  Medium: '#d97706',
  Low:    '#16a34a',
};

export type ArtifactStatus = 'Pending' | 'Approved' | 'Overridden';

export const status: Record<ArtifactStatus, string> = {
  Pending:    '#fef3c7',
  Approved:   '#dcfce7',
  Overridden: '#ede9fe',
};

// Framework Mapping — timing-category cards. Jarvis palette (amber → lavender → super-dark-purple)
// so the category strip reads as the same product as the MA header, not the old navy/blue island.
export const catGradient: Record<1 | 2 | 3, string> = {
  1: 'linear-gradient(135deg,#fbbf24,#f59e0b)',   // Cat 1 · Immediate — amber
  2: 'linear-gradient(135deg,#a78bfa,#8b5cf6)',   // Cat 2 · Near-term — lavender
  3: 'linear-gradient(135deg,#454F83,#221551)',   // Cat 3 · Deferred — dusky → super-dark-purple
};

// Framework Mapping — four-box cards (handoff §5.3).
export const boxStyle: Record<1 | 2 | 3 | 4, { bg: string; border: string; title: string }> = {
  1: { bg: '#fef2f2', border: '#fecaca', title: '#991b1b' },   // Full rebuild — HIGH
  2: { bg: '#fef3c7', border: '#fde68a', title: '#78350f' },   // Governed / light SDLC — MEDIUM
  3: { bg: '#eaf1ff', border: '#c7d7ff', title: '#1e3a8a' },   // Platform migration — HIGH
  4: { bg: '#dcfce7', border: '#a7f3c8', title: '#14532d' },   // Retain & improve — LOW
};

// Engagement Phases — artifact format chips (handoff §5.4).
export const formatChip: Record<string, { bg: string; fg: string }> = {
  PDF:  { bg: '#fee2e2', fg: '#991b1b' },
  DOCX: { bg: '#eaf1ff', fg: '#1e40af' },
  XLSX: { bg: '#dcfce7', fg: '#166534' },
  CSV:  { bg: '#dcfce7', fg: '#166534' },
  JSON: { bg: '#f5f3ff', fg: '#5b21b6' },
  MD:   { bg: '#f1f2f8', fg: '#334155' },
  PBIX: { bg: '#fef3c7', fg: '#92400e' },
  ZIP:  { bg: '#ede9fe', fg: '#5b21b6' },
};
